import React, { useContext, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../providers/AuthContext';
import { CategoryContext } from '../../providers/CategoryContext';

const ORIGINALES_CATEGORIES = ['Remeras', 'Almohadones', 'Cuadros', 'AI', 'Canguros'];
const DEDICATED_CATEGORIES = ['Personalizados', 'Personalizados IA', 'Merchandising', 'Niños'];

const Menu = ({ isAdminAuthenticated }) => {
  const { user, logout } = useContext(AuthContext);
  const { categories } = useContext(CategoryContext);
  const cartItems = useSelector(state => state.cart.items);
  const totalItems = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});

  const isOnDashboard = isAdminAuthenticated && location.pathname === '/dashboard';

  const handleAdminSidebarToggle = () => {
    window.dispatchEvent(new CustomEvent('toggleDashboardSidebar'));
  };

  // Categorías que van dentro del dropdown "Originales"
  const getActiveSubcategories = (category) =>
    (category.subcategories || []).filter(sub => (sub.productCount || 0) > 0);

  const { filteredOriginales, ninosCategory, otherCategories } = useMemo(() => ({
    filteredOriginales: categories.filter(cat => ORIGINALES_CATEGORIES.includes(cat.name)),
    ninosCategory: categories.find(cat => cat.name === 'Niños'),
    otherCategories: categories.filter(cat => !ORIGINALES_CATEGORIES.includes(cat.name) && !DEDICATED_CATEGORIES.includes(cat.name))
  }), [categories]);

  // Extrae solo el primer nombre del displayName
  const firstName = user?.displayName?.split(' ')[0] || 'Usuario';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const categoryHref = (categoryName, subcategoryName) => {
    const params = new URLSearchParams({ category: categoryName });
    if (subcategoryName) params.set('subcategory', subcategoryName);
    return `/tienda?${params.toString()}`;
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: '0',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    minWidth: '200px',
    padding: '8px 0',
    zIndex: 1001
  };

  const dropdownItemStyle = {
    display: 'block',
    padding: '10px 16px',
    color: 'white',
    textDecoration: 'none',
    transition: 'background 0.2s'
  };

  const renderDropdownItems = (category, activeSubs, onNavigate) => (
    <>
      <Link
        to={categoryHref(category.name)}
        className="dropdown-item-custom"
        style={dropdownItemStyle}
        onClick={onNavigate}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(31, 142, 56, 0.3)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <i className={`${category.icon || 'fa-solid fa-tag'} me-2`}></i> Ver todos
      </Link>
      {activeSubs.map(sub => (
        <Link
          key={sub._id || sub.name}
          to={categoryHref(category.name, sub.name)}
          className="dropdown-item-custom"
          style={dropdownItemStyle}
          onClick={onNavigate}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(31, 142, 56, 0.3)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <i className="fa-solid fa-shirt me-2"></i> {sub.name}
        </Link>
      ))}
    </>
  );

  const renderCategoryNav = (category, { mobile = false } = {}) => {
    const activeSubs = getActiveSubcategories(category);
    const hasSubs = activeSubs.length > 0;
    const dropdownKey = category._id;

    if (!hasSubs) {
      if (mobile) {
        return (
          <Link
            key={category._id}
            to={categoryHref(category.name)}
            className="mobile-nav-link"
            onClick={toggleMobileMenu}
          >
            <i className={`${category.icon || 'fa-solid fa-tag'} me-2`}></i> {category.name}
          </Link>
        );
      }

      return (
        <Link
          key={category._id}
          to={categoryHref(category.name)}
          className="nav-link"
        >
          <i className={`${category.icon || 'fa-solid fa-tag'} me-1`}></i> {category.name}
        </Link>
      );
    }

    if (mobile) {
      const isExpanded = Boolean(mobileExpanded[dropdownKey]);
      return (
        <div key={category._id}>
          <button
            type="button"
            className="mobile-nav-link"
            onClick={() => setMobileExpanded(prev => ({ ...prev, [dropdownKey]: !prev[dropdownKey] }))}
            style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', color: 'white' }}
          >
            <i className={`${category.icon || 'fa-solid fa-tag'} me-2`}></i> {category.name}
            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} ms-2`} style={{ fontSize: '10px' }}></i>
          </button>
          {isExpanded && (
            <>
              <Link
                to={categoryHref(category.name)}
                className="mobile-nav-link"
                onClick={toggleMobileMenu}
                style={{ paddingLeft: '30px' }}
              >
                Ver todos
              </Link>
              {activeSubs.map(sub => (
                <Link
                  key={sub._id || sub.name}
                  to={categoryHref(category.name, sub.name)}
                  className="mobile-nav-link"
                  onClick={toggleMobileMenu}
                  style={{ paddingLeft: '30px' }}
                >
                  {sub.name}
                </Link>
              ))}
            </>
          )}
        </div>
      );
    }

    return (
      <div
        key={category._id}
        className="nav-dropdown"
        style={{ position: 'relative' }}
        onMouseEnter={() => setOpenDropdown(dropdownKey)}
        onMouseLeave={() => setOpenDropdown(null)}
      >
        <button
          type="button"
          className="nav-link"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: 'inherit'
          }}
        >
          <i className={`${category.icon || 'fa-solid fa-tag'} me-1`}></i> {category.name}
          <i
            className="fa-solid fa-chevron-down ms-1"
            style={{ fontSize: '10px', transition: 'transform 0.2s', transform: openDropdown === dropdownKey ? 'rotate(180deg)' : 'rotate(0)' }}
          ></i>
        </button>
        {openDropdown === dropdownKey && (
          <div style={dropdownStyle}>
            {renderDropdownItems(category, activeSubs)}
          </div>
        )}
      </div>
    );
  };

  const personalizedLinks = [
    { to: '/personalizados', icon: 'fa-compass', label: 'Ver Personalizados' },
    { to: '/tienda?category=Personalizados', icon: 'fa-shirt', label: 'Personalizar un producto' },
    { to: '/personalizados/ia', icon: 'fa-wand-magic-sparkles', label: 'Crear con IA' },
    { to: '/merchandising', icon: 'fa-building', label: 'Merchandising' }
  ];

  const renderPersonalizadosNav = (mobile = false) => {
    if (mobile) {
      const isExpanded = Boolean(mobileExpanded.personalizados);
      return (
        <div className="mobile-personalizados-nav">
          <button
            type="button"
            className="mobile-nav-link"
            onClick={() => setMobileExpanded(previous => ({ ...previous, personalizados: !previous.personalizados }))}
            aria-expanded={isExpanded}
            aria-controls="mobile-personalizados-links"
            style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', color: 'white' }}
          >
            <i className="fa-solid fa-pen-ruler me-2"></i> Personalizados
            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} ms-2`} style={{ fontSize: '10px' }}></i>
          </button>
          {isExpanded && (
            <div id="mobile-personalizados-links">
              {personalizedLinks.map(link => (
                <Link key={link.to} to={link.to} className="mobile-nav-link" onClick={toggleMobileMenu} style={{ paddingLeft: '30px' }}>
                  <i className={`fa-solid ${link.icon} me-2`}></i>{link.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    const isOpen = openDropdown === 'personalizados';
    return (
      <div
        className="nav-dropdown"
        style={{ position: 'relative' }}
        onMouseEnter={() => setOpenDropdown('personalizados')}
        onMouseLeave={() => setOpenDropdown(null)}
      >
        <button
          type="button"
          className="nav-link"
          onClick={() => setOpenDropdown(isOpen ? null : 'personalizados')}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <i className="fa-solid fa-pen-ruler me-1"></i> Personalizados
          <i className="fa-solid fa-chevron-down ms-1" style={{ fontSize: '10px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}></i>
        </button>
        {isOpen && (
          <div style={{ ...dropdownStyle, minWidth: '245px' }} role="menu">
            {personalizedLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="dropdown-item-custom"
                style={dropdownItemStyle}
                role="menuitem"
                onClick={() => setOpenDropdown(null)}
                onMouseEnter={event => { event.currentTarget.style.background = 'rgba(31, 142, 56, 0.3)'; }}
                onMouseLeave={event => { event.currentTarget.style.background = 'transparent'; }}
              >
                <i className={`fa-solid ${link.icon} me-2`}></i>{link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <nav
        id="barramenu"
        className="enhanced-header"
        style={{
          padding: '15px 20px',
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          position: 'sticky',
          top: 0,
          zIndex: 1002,
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Admin sidebar toggle — only on /dashboard on mobile */}
        {isOnDashboard && (
          <button
            className="dashboard-admin-toggle"
            onClick={handleAdminSidebarToggle}
            aria-label="Abrir menú del panel"
          >
            <i className="fas fa-bars"></i>
          </button>
        )}

        {/* Logo y Marca */}
        <div className="logo-section" style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'white' }}>
            <img
              src="/images/RemEnterprisesBlanco.png"
              alt="REM Enterprises"
              className="header-logo-img"
            />
          </Link>
        </div>

        {/* Menú Desktop */}
        <div className="desktop-menu" style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/tienda" className="nav-link">
            <i className="fa-solid fa-store me-1"></i> Tienda
          </Link>

          {/* Dropdown Originales */}
          {filteredOriginales.length > 0 && (
            <div
              className="nav-dropdown"
              style={{ position: 'relative' }}
              onMouseEnter={() => setOpenDropdown('originales')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                className="nav-link"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <i className="fa-solid fa-star me-1"></i> Originales
                <i className={`fa-solid fa-chevron-down ms-1`} style={{ fontSize: '10px', transition: 'transform 0.2s', transform: openDropdown === 'originales' ? 'rotate(180deg)' : 'rotate(0)' }}></i>
              </button>
              {openDropdown === 'originales' && (
                <div style={dropdownStyle}>
                  {filteredOriginales.map(category => (
                    <Link
                      key={category._id}
                      to={categoryHref(category.name)}
                      className="dropdown-item-custom"
                      style={dropdownItemStyle}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(31, 142, 56, 0.3)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <i className={`${category.icon} me-2`}></i> {category.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Otras categorías */}
          {otherCategories.map(category => renderCategoryNav(category))}
          {renderPersonalizadosNav()}
          {ninosCategory && renderCategoryNav(ninosCategory)}
          <Link to="/cart" className="nav-link cart-link">
            <i className="fa-solid fa-cart-shopping me-1"></i>
            Carrito
            {totalItems > 0 && (
              <span className="cart-badge">{totalItems}</span>
            )}
          </Link>
        </div>

        {/* Botón Hamburguesa Mobile */}
        <button
          className={`modern-hamburger d-md-none ${isMobileMenuOpen ? 'is-active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Sección Usuario Desktop */}
        <div className="user-section d-none d-md-flex" style={{ display: 'flex', alignItems: 'center' }}>
          {user ? (
            <>
              <Link
                to="/profile"
                className="nav-link user-link"
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="Avatar"
                    style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '8px' }}
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    backgroundColor: '#1f8e38',
                    color: 'white',
                    display: user.photoURL ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {firstName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                {firstName}
              </Link>
              <button
                onClick={() => { logout(); navigate('/user'); }}
                className="nav-link logout-btn"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  marginLeft: '10px'
                }}
              >
                <i className="fa-solid fa-sign-out-alt me-1"></i>
              </button>
            </>
          ) : (
            <Link to="/user" className="nav-link">
              <i className="fa-solid fa-user me-1"></i>
              Iniciar sesión
            </Link>
          )}
          {isAdminAuthenticated && (
            <Link to="/dashboard" className="nav-link admin-link">
              <i className="fa-solid fa-chart-line me-1"></i>
            </Link>
          )}
        </div>
      </nav>

      {/* Sidebar Mobile */}
      {isMobileMenuOpen && (
        <div className="mobile-sidebar" style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '280px',
          height: '100dvh',
          boxSizing: 'border-box',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
          zIndex: 1001,
          transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          padding: '20px',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          boxShadow: '-5px 0 20px rgba(0,0,0,0.5)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', marginTop: '60px' }}>
            <h5 style={{ color: 'white', margin: 0 }}>Menú Principal</h5>
          </div>

          <div className="mobile-nav-links">
            <Link to="/" className="mobile-nav-link" onClick={toggleMobileMenu}>
              <i className="fa-solid fa-house me-2"></i> Inicio
            </Link>
            <Link to="/tienda" className="mobile-nav-link" onClick={toggleMobileMenu}>
              <i className="fa-solid fa-store me-2"></i> Tienda
            </Link>

            {/* Sección Originales en Mobile */}
            {filteredOriginales.length > 0 && (
              <>
                <div className="mobile-nav-link" style={{ color: '#1f8e38', fontWeight: 'bold', cursor: 'default' }}>
                  <i className="fa-solid fa-star me-2"></i> Originales
                </div>
                {filteredOriginales.map(category => (
                  <Link
                    key={category._id}
                    to={categoryHref(category.name)}
                    className="mobile-nav-link"
                    onClick={toggleMobileMenu}
                    style={{ paddingLeft: '30px' }}
                  >
                    <i className={`${category.icon} me-2`}></i> {category.name}
                  </Link>
                ))}
              </>
            )}

            {/* Otras categorías en Mobile */}
            {otherCategories.map(category => renderCategoryNav(category, { mobile: true }))}
            {renderPersonalizadosNav(true)}
            {ninosCategory && renderCategoryNav(ninosCategory, { mobile: true })}
            <Link to="/cart" className="mobile-nav-link" onClick={toggleMobileMenu}>
              <i className="fa-solid fa-cart-shopping me-2"></i>
              Carrito {totalItems > 0 && `(${totalItems})`}
            </Link>

            <hr style={{ borderColor: '#333', margin: '20px 0' }} />

            {user ? (
              <>
                <Link to="/profile" className="mobile-nav-link" onClick={toggleMobileMenu}>
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt="Avatar"
                      style={{ width: '20px', height: '20px', borderRadius: '50%', marginRight: '8px' }}
                      onError={e => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#1f8e38',
                      color: 'white',
                      display: user.photoURL ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '8px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    {firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  {firstName}
                </Link>
                <button
                  onClick={() => { logout(); navigate('/user'); toggleMobileMenu(); }}
                  className="mobile-nav-link"
                  style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
                >
                  <i className="fa-solid fa-sign-out-alt me-2"></i>
                  Cerrar sesión
                </button>
              </>
            ) : (
              <Link to="/user" className="mobile-nav-link" onClick={toggleMobileMenu}>
                <i className="fa-solid fa-user me-2"></i>
                Iniciar sesión
              </Link>
            )}
            {isAdminAuthenticated && (
              <Link to="/dashboard" className="mobile-nav-link" onClick={toggleMobileMenu}>
                <i className="fa-solid fa-chart-line me-2"></i> Dashboard
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Overlay para cerrar sidebar */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={toggleMobileMenu}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000
          }}
        />
      )}
    </>
  );
};

export default Menu;
